import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@components/ui/Button/Button';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsCrossOrgSnapshotService } from '@services/dsCrossOrgSnapshotService';
import { useTranslation } from '@hooks/useTranslation';
import { useAuthStore } from '@stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { toTitleCase } from '@utils/textCase';
import { getReviewerBadgeClass } from '@utils/reviewerStatus';
import type { DocumentReviewer } from '@workpay/ds-shared/types/review';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { AddReviewerModal } from './AddReviewerModal';
import styles from './SnapshotReviewPanel.module.scss';

interface SnapshotReviewPanelProps {
  snapshotId: string;
}

const ACTIVITY_ACTION_LABEL: Record<string, string> = {
  REVIEW_STARTED: 'Review started',
  REVIEWER_RESPONSE: 'Reviewer responded',
  AUTHOR_RESPONSE: 'Author responded',
  REVIEWER_DONE: 'Reviewer marked complete',
  REVIEW_COMPLETED: 'Review completed',
  NOTE_ADDED: 'Note added',
  TOKEN_REGENERATED: 'Token regenerated',
};

export function SnapshotReviewPanel({ snapshotId }: SnapshotReviewPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [showAddReviewer, setShowAddReviewer] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<DocumentReviewer | null>(null);
  const [markCompleteTarget, setMarkCompleteTarget] = useState<DocumentReviewer | null>(null);
  const [regenerateTarget, setRegenerateTarget] = useState<DocumentReviewer | null>(null);

  const { data: review, isLoading, error: reviewError } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-review', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getReview(snapshotId),
  });

  const { data: activities } = useQuery({
    queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId],
    queryFn: () => dsCrossOrgSnapshotService.getReviewActivities(snapshotId),
  });

  const invalidateReview = () => {
    void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review', snapshotId] });
    void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot-review-activities', snapshotId] });
  };

  const addReviewerMutation = useMutation({
    mutationFn: (data: { name: string; email: string }) => dsCrossOrgSnapshotService.addReviewer(snapshotId, data),
    onSuccess: () => {
      invalidateReview();
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_REVIEWER_ADDED', 'Reviewer added') });
      setShowAddReviewer(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const removeReviewerMutation = useMutation({
    mutationFn: (reviewerId: string) => dsCrossOrgSnapshotService.removeReviewer(snapshotId, reviewerId),
    onSuccess: () => {
      invalidateReview();
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_REVIEWER_REMOVED', 'Reviewer removed') });
      setRemoveTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: (reviewerId: string) => dsCrossOrgSnapshotService.markReviewerComplete(snapshotId, reviewerId),
    onSuccess: () => {
      invalidateReview();
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_REVIEWER_MARKED', 'Reviewer marked as complete') });
      setMarkCompleteTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (reviewerId: string) => dsCrossOrgSnapshotService.regenerateToken(snapshotId, reviewerId),
    onSuccess: () => {
      invalidateReview();
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_TOKEN_REGENERATED', 'Token regenerated — new 30-day link issued') });
      setRegenerateTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const isTokenExpired = (reviewer: DocumentReviewer): boolean => {
    if (!reviewer.tokenExpiresAt) return false;
    return new Date(reviewer.tokenExpiresAt).getTime() < Date.now();
  };

  const copyReviewLink = (reviewer: DocumentReviewer) => {
    if (!reviewer.accessToken) {
      addNotification({ variant: 'critical', title: t('K_SNAPSHOT_ADMIN_NO_TOKEN', 'No access token available') });
      return;
    }
    if (isTokenExpired(reviewer)) {
      addNotification({
        variant: 'critical',
        title: t('K_SNAPSHOT_ADMIN_TOKEN_EXPIRED_COPY', 'Token has expired. Regenerate the link first.'),
      });
      return;
    }
    // VITE_DS_EXTERNAL_URL is set per-environment via docker-compose or .env.development.
    // TODO: Revisit when we have a cleaner multi-env config story.
    const externalUrl = import.meta.env.VITE_DS_EXTERNAL_URL;
    if (!externalUrl) {
      addNotification({
        variant: 'critical',
        title: t('K_SNAPSHOT_ADMIN_NO_EXTERNAL_URL', 'Review portal URL not configured'),
      });
      return;
    }
    const reviewUrl = `${externalUrl}/review/${reviewer.accessToken}`;
    void navigator.clipboard.writeText(reviewUrl).then(() => {
      addNotification({ variant: 'success', title: t('K_SNAPSHOT_ADMIN_LINK_COPIED', 'Review link copied to clipboard') });
    }).catch(() => {
      addNotification({ variant: 'critical', title: t('K_SNAPSHOT_ADMIN_LINK_COPY_FAILED', 'Failed to copy link') });
    });
  };

  const reviewerActions = (reviewer: DocumentReviewer) => {
    const actions: ProfileAction[] = [];
    if (reviewer.accessToken) {
      actions.push({
        id: 'copy-link',
        label: t('K_SNAPSHOT_ADMIN_COPY_LINK', 'Copy Review Link'),
        onClick: () => copyReviewLink(reviewer),
      });
    }
    if (hasPermission('CONTRACT_TEMPLATES_UPDATE')) {
      if (reviewer.status !== 'REVIEWED') {
        actions.push({
          id: 'mark-complete',
          label: t('K_SNAPSHOT_ADMIN_MARK_REVIEWER', 'Mark as Complete'),
          onClick: () => setMarkCompleteTarget(reviewer),
        });
      }
      actions.push({
        id: 'regenerate',
        label: t('K_SNAPSHOT_ADMIN_REGEN_TOKEN', 'Regenerate Link'),
        onClick: () => setRegenerateTarget(reviewer),
      });
      actions.push({
        id: 'remove',
        label: t('K_SNAPSHOT_ADMIN_REMOVE_REVIEWER', 'Remove'),
        onClick: () => setRemoveTarget(reviewer),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  };

  // Forbidden (inline — surrounding SnapshotAdminDetail chrome stays usable).
  if (isForbiddenError(reviewError)) {
    return <Forbidden variant="inline" hideAction />;
  }

  if (isLoading) {
    return <div className={styles.loading}>{t('K_LOADING', 'Loading...')}</div>;
  }

  if (!review) {
    return <div className={styles.empty}>{t('K_SNAPSHOT_ADMIN_NO_REVIEW', 'No active review for this snapshot.')}</div>;
  }

  const isReviewActive = review.status === 'IN_PROGRESS' || review.status === 'PENDING';

  return (
    <>
      <div className={styles.reviewerHeader}>
        <div className={styles.reviewStatus}>
          <span className={styles.reviewStatusLabel}>{t('K_REVIEW_STATUS', 'Review Status')}</span>
          <span className={`${styles.badge} ${styles.badgeInfo}`}>{toTitleCase(review.status)}</span>
        </div>
        {isReviewActive && hasPermission('CONTRACT_TEMPLATES_UPDATE') && (
          <Button variant="outline" size="sm" onClick={() => setShowAddReviewer(true)}>
            {t('K_SNAPSHOT_ADMIN_ADD_REVIEWER', 'Add Reviewer')}
          </Button>
        )}
      </div>

      <div className={styles.reviewerList}>
        {review.reviewers.length === 0 ? (
          <p className={styles.emptyText}>{t('K_SNAPSHOT_ADMIN_NO_REVIEWERS', 'No reviewers assigned.')}</p>
        ) : (
          review.reviewers.map((reviewer) => (
            <div key={reviewer.id} className={styles.reviewerCard}>
              <div className={styles.reviewerInfo}>
                <span className={styles.reviewerName}>{reviewer.name}</span>
                <span className={styles.reviewerEmail}>{reviewer.email}</span>
                {reviewer.tokenExpiresAt && (() => {
                  const expiresAt = new Date(reviewer.tokenExpiresAt);
                  const expired = expiresAt.getTime() < Date.now();
                  return (
                    <span className={`${styles.reviewerMeta} ${expired ? styles.reviewerMetaExpired : ''}`}>
                      {(expired
                        ? t('K_SNAPSHOT_ADMIN_TOKEN_EXPIRED', 'Token expired on {date}')
                        : t('K_SNAPSHOT_ADMIN_TOKEN_EXPIRES', 'Token expires: {date}')
                      ).replace('{date}', expiresAt.toLocaleDateString())}
                    </span>
                  );
                })()}
              </div>
              <span className={`${styles.badge} ${getReviewerBadgeClass(reviewer.status, styles)}`}>
                {toTitleCase(reviewer.status)}
              </span>
              {reviewerActions(reviewer)}
            </div>
          ))
        )}
      </div>

      {activities && activities.length > 0 && (
        <div className={styles.activitySection}>
          <h4 className={styles.activityTitle}>{t('K_SNAPSHOT_ADMIN_ACTIVITY', 'Activity')}</h4>
          <div className={styles.activityList}>
            {activities.map((activity) => (
              <div key={activity.id} className={styles.activityItem}>
                <div className={styles.activityHeader}>
                  <span className={styles.activityAction}>
                    {t(`K_ACTIVITY_${activity.action}`, ACTIVITY_ACTION_LABEL[activity.action] ?? activity.action)}
                  </span>
                  <span className={styles.activityTime}>
                    {new Date(activity.createdOn).toLocaleString()}
                  </span>
                </div>
                <span className={styles.activityActor}>
                  {activity.actorName} &middot; {activity.actorEmail}
                </span>
                {activity.message && (
                  <p className={styles.activityMessage}>{activity.message}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <AddReviewerModal
        isOpen={showAddReviewer}
        onClose={() => setShowAddReviewer(false)}
        onConfirm={(data) => addReviewerMutation.mutate(data)}
        loading={addReviewerMutation.isPending}
      />

      <AlertModal
        variant="danger"
        title={t('K_SNAPSHOT_ADMIN_REMOVE_REVIEWER', 'Remove Reviewer')}
        description={t('K_SNAPSHOT_ADMIN_REMOVE_REVIEWER_DESC', 'Remove "{name}" from this review? Their review link will stop working.').replace('{name}', removeTarget?.name ?? '')}
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        primaryLabel={t('K_REMOVE', 'Remove')}
        onPrimaryClick={() => { if (removeTarget) removeReviewerMutation.mutate(removeTarget.id); }}
        loading={removeReviewerMutation.isPending}
      />

      <AlertModal
        variant="warning"
        title={t('K_SNAPSHOT_ADMIN_MARK_REVIEWER_TITLE', 'Mark Reviewer Complete')}
        description={t('K_SNAPSHOT_ADMIN_MARK_REVIEWER_DESC', 'Mark "{name}" as having completed the review on their behalf?').replace('{name}', markCompleteTarget?.name ?? '')}
        isOpen={!!markCompleteTarget}
        onClose={() => setMarkCompleteTarget(null)}
        primaryLabel={t('K_SNAPSHOT_ADMIN_MARK_REVIEWER', 'Mark as Complete')}
        onPrimaryClick={() => { if (markCompleteTarget) markCompleteMutation.mutate(markCompleteTarget.id); }}
        loading={markCompleteMutation.isPending}
      />

      <AlertModal
        variant="warning"
        title={t('K_SNAPSHOT_ADMIN_REGEN_TOKEN', 'Regenerate Link')}
        description={t('K_SNAPSHOT_ADMIN_REGEN_TOKEN_DESC', 'Generate a fresh 30-day review link for "{name}"? The old link will stop working.').replace('{name}', regenerateTarget?.name ?? '')}
        isOpen={!!regenerateTarget}
        onClose={() => setRegenerateTarget(null)}
        primaryLabel={t('K_SNAPSHOT_ADMIN_REGEN_TOKEN', 'Regenerate')}
        onPrimaryClick={() => { if (regenerateTarget) regenerateMutation.mutate(regenerateTarget.id); }}
        loading={regenerateMutation.isPending}
      />
    </>
  );
}
